import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const services = [
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Conseil en gestion',
    description:
      "Accompagnement dans la structuration et l'organisation de votre activité pour gagner en efficacité et en clarté.",
  },
  {
    icon: 'ri-compass-3-line',
    title: 'Conseil stratégique',
    description:
      "Aide à la prise de décision et au développement de l'entreprise, avec une vision claire des objectifs à atteindre.",
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Optimisation des processus',
    description:
      "Analyse et amélioration des performances pour réduire les coûts, fluidifier les opérations et maximiser les résultats.",
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f8d2965d25794b27dab2378586aa83ca.jpg"
            alt="KRUTAK background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-900/60 to-navy-900/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full py-32">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-gray-300 mb-6">
              Conseil en Affaires · Lille, France
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Conseil en gestion et développement d'entreprise
            </h1>
            <p className="text-lg text-gray-200 leading-relaxed mb-10 max-w-xl">
              KRUTAK accompagne les entrepreneurs et les entreprises dans leur développement et leur organisation. Des solutions simples, efficaces et adaptées à chaque situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 bg-white text-navy-800 text-[15px] font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-200"
              >
                Contactez-nous
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-navy-800" />
                </span>
              </Link>
              <Link
                to="/a-propos"
                className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 border border-white/50 text-white text-[15px] font-medium px-8 py-4 rounded-lg hover:border-white hover:bg-white/10 transition-all duration-200"
              >
                En savoir plus
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-down-line text-white/60 text-lg" />
          </span>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white" id="services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-navy-700 mb-3 block">
              Nos services
            </span>
            <h2 className="text-4xl font-bold text-navy-800 mb-4">
              Des solutions adaptées à vos besoins
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
              Chaque mission est conduite avec rigueur pour apporter des résultats concrets et durables.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <div
                key={i}
                className="group bg-white border border-gray-100 rounded-2xl p-10 hover:border-navy-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-navy-50 mb-6 group-hover:bg-navy-800 transition-colors duration-300">
                  <i className={`${service.icon} text-2xl text-navy-700 group-hover:text-white transition-colors duration-300`} />
                </div>
                <h3 className="text-xl font-bold text-navy-800 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-500 text-[15px] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approche */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-navy-700 mb-4 block">
                Notre approche
              </span>
              <h2 className="text-4xl font-bold text-navy-800 mb-6 leading-tight">
                Une méthode rigoureuse au service de votre réussite
              </h2>
              <p className="text-gray-600 text-[17px] leading-relaxed mb-6">
                Chaque mission est réalisée avec sérieux et méthode afin d'apporter des solutions concrètes et adaptées aux besoins du client.
              </p>
              <p className="text-gray-600 text-[17px] leading-relaxed mb-8">
                L'objectif est d'accompagner les entrepreneurs dans leurs décisions stratégiques et d'améliorer durablement la performance de leur entreprise.
              </p>
              <ul className="space-y-4">
                {[
                  'Diagnostic précis de la situation',
                  'Recommandations personnalisées',
                  'Accompagnement dans la mise en œuvre',
                  'Suivi des résultats',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-check-line text-navy-700 text-lg" />
                    </span>
                    <span className="text-gray-600 text-[15px]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20business%20meeting%20office%20Lille%20France%20two%20people%20discussing%20strategy%20documents%20on%20table%20minimalist%20clean%20interior%20modern%20workspace%20natural%20light%20white%20walls%20professional%20consultation%20session&width=800&height=600&seq=krutak-approach-1&orientation=landscape"
                  alt="Approche KRUTAK"
                  className="w-full h-80 object-cover object-top rounded-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-navy-800 rounded-lg">
                    <i className="ri-trophy-line text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-navy-800 font-bold text-lg">Résultats concrets</p>
                    <p className="text-gray-500 text-sm">Adaptés à chaque situation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secteurs */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-navy-700 mb-3 block">
              Pourquoi KRUTAK
            </span>
            <h2 className="text-4xl font-bold text-navy-800 mb-4">
              Une expertise à votre service
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-user-heart-line', label: 'Approche personnalisée', desc: 'Chaque client est unique, chaque solution est sur mesure.' },
              { icon: 'ri-shield-check-line', label: 'Indépendance garantie', desc: 'Entrepreneur individuel, sans conflit d\'intérêt.' },
              { icon: 'ri-map-pin-2-line', label: 'Basé à Lille', desc: 'Interventions locales et à distance dans toute la France.' },
              { icon: 'ri-time-line', label: 'Réactivité', desc: 'Des réponses rapides et un accompagnement de proximité.' },
            ].map((item, i) => (
              <div key={i} className="text-center p-8 rounded-xl bg-gray-50">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-navy-100">
                  <i className={`${item.icon} text-2xl text-navy-700`} />
                </div>
                <h4 className="font-bold text-navy-800 text-[15px] mb-2">{item.label}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-navy-800">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Prêt à développer votre entreprise ?
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Contactez-nous pour échanger sur votre projet et découvrir comment KRUTAK peut vous accompagner.
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 bg-white text-navy-800 text-[15px] font-semibold px-10 py-4 rounded-lg hover:bg-gray-100 transition-all duration-200"
          >
            Prendre contact
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-navy-800" />
            </span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
